import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Textarea variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const textareaVariants = cva(
  [
    // Base styles - structural only (size variant owns padding, font-size, min-height)
    'flex w-full',
    'font-body',
    'rounded-field',
    'text-body',
    'placeholder:text-subtext',
    'transition-colors duration-200',
    // Focus styles - visible focus ring per WCAG 2.1 AA
    'focus-visible:outline-none',
    'focus-visible:ring-0',
    'focus-visible:ring-offset-0',
    // Disabled styles
    'disabled:cursor-not-allowed',
    'disabled:opacity-60',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-surface-field',
          // Value text uses text-body (themeable) so it is not locked to purple
          'text-body',
          'border border-border-field',
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          'disabled:bg-neutral-100',
          'disabled:border-border-field',
          'disabled:text-subtext',
        ],
        'two-line': [
          'bg-surface-field',
          // Value text uses text-body (themeable) so it is not locked to purple
          'text-body',
          'border border-border-field',
          'hover:border-border-field-hover',
          'focus-visible:border-border-field-focus',
          'focus-visible:ring-field-focus',
          'focus-visible:ring-field-focus-width',
          'disabled:bg-neutral-100',
          'disabled:border-border-field',
          'disabled:text-subtext',
        ],
      },
      size: {
        sm: 'text-b5 px-xs py-xxs min-h-[64px]',
        md: 'text-b5 px-xs py-xs min-h-[88px]',
        lg: 'text-b4 px-sm py-xs min-h-[120px]',
      },
      error: {
        true: [
          'border-error-500',
          'text-error-500',
          'hover:border-error-500',
          'focus-visible:border-error-500',
          'focus-visible:ring-2',
          'focus-visible:ring-error-100',
        ],
        false: '',
      },
      resize: {
        none: 'resize-none',
        vertical: 'resize-y',
        both: 'resize',
      },
    },
    compoundVariants: [
      {
        // Top padding holds the absolute field name. Bottom padding is 0,
        // matching a filled comment field. Side padding comes from the size.
        variant: 'two-line',
        className: 'pt-lg pb-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
      error: false,
      resize: 'vertical',
    },
  },
);

export type TextareaVariantProps = VariantProps<typeof textareaVariants>;
